import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Cloud, Wifi, Users, Box, RefreshCw, MapPin, Server, MoreHorizontal,
  CheckCircle2, FileText, Fingerprint,
  ShieldCheck, Clock, HardDrive, Rocket, Check, AlertTriangle, Circle,
} from "lucide-react";
import { API_URL } from "../config";

// ---- Static data ---------------------------------------------------------
const TOP_TABS = ["CI/CD Pipelines", "OTA Deployment", "Downloads", "REST API"];

const PLATFORMS = [
  { id: "aws", name: "AWS IoT Core", sub: "AWS IoT", icon: Cloud },
  { id: "azure", name: "Azure IoT Hub", sub: "Microsoft Azure", icon: Wifi },
  { id: "mender", name: "Mender", sub: "Mender.io", icon: Users },
  { id: "balena", name: "BalenaCloud", sub: "Balena", icon: Box },
  { id: "swupdate", name: "SWUpdate", sub: "SWUpdate", icon: RefreshCw },
  { id: "hawkbit", name: "Eclipse hawkBit", sub: "hawkBit", icon: MapPin },
  { id: "custom", name: "Custom Server", sub: "HTTP/HTTPS", icon: Server },
  { id: "other", name: "Other Platform", sub: "More options", icon: MoreHorizontal },
];

// Only AWS has a real dispatch implementation on the backend right now
// (crypto/ota_routes.py: PLATFORM_HANDLERS). Every other platform still
// records a deployment row but is honestly reported as "not_configured" —
// see the status banner after clicking Start Deployment.
const PLATFORMS_WITH_LIVE_DISPATCH = ["aws"];

const STEPS = [
  "Select Device Platform",
  "Select Device Group",
  "Deployment Settings",
  "Review & Confirm",
];

const STRATEGIES = ["Rolling Update", "Canary", "All at Once"];
const BATCH_SIZES = ["5%", "10%", "25%", "50%", "100%"];
const HEALTH_CHECK_OPTS = ["1 minute", "5 minutes", "15 minutes", "30 minutes"];
const MAX_RETRY_OPTS = ["1", "2", "3", "5"];

// ---- Helpers --------------------------------------------------------------
const TAB_ROUTES = {
  "CI/CD Pipelines": "/integration",
  Downloads: "/downloads",
  "REST API": "/rest-api",
};

const INITIAL_SETTINGS = {
  strategy: "Rolling Update",
  batchSize: "10%",
  healthCheck: "5 minutes",
  maxRetry: "3",
  rollback: true,
};

const isLive = (platformId) => PLATFORMS_WITH_LIVE_DISPATCH.includes(platformId);

// Real device-group registry — starts empty until rows exist in
// ota_device_groups (see ota_routes.py migration comment). No fabricated
// "2,458 devices" fallback.
const useDeviceGroups = (platform) => {
  const [deviceGroups, setDeviceGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [deviceGroup, setDeviceGroup] = useState("");

  useEffect(() => {
    setLoadingGroups(true);
    setDeviceGroup("");
    fetch(`${API_URL}/api/ota/device-groups?platform=${platform}`)
      .then((res) => res.json())
      .then((data) => {
        const groups = data.device_groups || [];
        setDeviceGroups(groups);
        if (groups.length > 0) setDeviceGroup(groups[0].name);
      })
      .catch(() => setDeviceGroups([]))
      .finally(() => setLoadingGroups(false));
  }, [platform]);

  return { deviceGroups, loadingGroups, deviceGroup, setDeviceGroup };
};

const buildSummaryItems = ({ manifest, algorithm, selectedMethod, signedAt, hashAlgorithm, selectedGroup }) => [
  { icon: FileText, label: "Firmware", value: manifest?.firmware?.file_name || "—" },
  {
    icon: FileText,
    label: "Firmware Size",
    value: manifest ? `${(manifest.firmware.file_size / 1024 / 1024).toFixed(2)} MB` : "—",
  },
  { icon: Fingerprint, label: "Signature Algorithm", value: algorithm || "—" },
  { icon: ShieldCheck, label: "Identity Source", value: selectedMethod?.title || "—" },
  { icon: Clock, label: "Signed On", value: signedAt ? new Date(signedAt).toLocaleString() : "—" },
  { icon: HardDrive, label: "Hash Algorithm", value: hashAlgorithm || "—" },
  {
    icon: Users,
    label: "Target Devices",
    value: selectedGroup ? `${selectedGroup.device_count} devices` : "No group registered",
  },
];

// ---- Presentational components -------------------------------------------
const TopTabs = ({ activeTab, onSelect }) => (
  <div style={styles.topTabs}>
    {TOP_TABS.map((tab) => {
      const active = activeTab === tab;
      return (
        <button
          key={tab}
          onClick={() => onSelect(tab)}
          style={{
            ...styles.topTabBtn,
            color: active ? "#5B4FE0" : "#8B92A8",
            borderBottom: active ? "2px solid #5B4FE0" : "2px solid transparent",
          }}
        >
          {tab}
        </button>
      );
    })}
  </div>
);

const Stepper = () => (
  <div className="stepper">
    {STEPS.map((label, i) => {
      const idx = i + 1;
      const current = idx === 1 ? "current" : "";
      return (
        <React.Fragment key={label}>
          <div className="step">
            <div className={`step-circle ${current}`}>{idx}</div>
            <div className={`step-label ${current}`}>{label}</div>
          </div>
          {idx !== STEPS.length && <div className="step-line" />}
        </React.Fragment>
      );
    })}
  </div>
);

const PlatformCard = ({ platform, selected, onSelect, thingGroupArn, onThingGroupArn }) => {
  const live = isLive(platform);
  const SelectedIcon = selected.icon;
  return (
    <section className="card">
      <h3 className="card-title">1. Select Device Platform</h3>
      <p className="card-sub">Choose your device platform or OTA server to deploy the firmware.</p>
      <div className="platform-grid">
        {PLATFORMS.map((p) => {
          const Icon = p.icon;
          const isActive = p.id === platform;
          return (
            <button
              key={p.id}
              className={`platform-tile ${isActive ? "active" : ""}`}
              onClick={() => onSelect(p.id)}
              type="button"
            >
              <div className="radio">{isActive && <div className="radio-dot" />}</div>
              <div className="platform-icon"><Icon size={20} /></div>
              <div className="platform-name">{p.name}</div>
              <div className="platform-sub">{p.sub}</div>
            </button>
          );
        })}
      </div>

      <div className="selected-details">
        <h4 className="selected-heading">Selected Platform</h4>
        <div className="selected-card">
          <div className="selected-top">
            <div className="selected-left">
              <div className="selected-icon"><SelectedIcon size={16} /></div>
              <div>
                <div className="selected-name">{selected.name}</div>
                <div className="selected-desc">
                  {live
                    ? "Live dispatch is implemented for this platform (requires AWS credentials configured on the server)."
                    : "No live integration is wired up for this platform yet — deployments here are recorded but not actually sent anywhere."}
                </div>
              </div>
            </div>
            <span className={live ? "badge-connected" : "badge-not-connected"}>
              {live ? <><Check size={12} /> Live</> : "Not wired up"}
            </span>
          </div>

          {platform === "aws" && (
            <div style={{ marginTop: 10 }}>
              <div className="field-label">AWS IoT Thing Group ARN (optional)</div>
              <input
                className="text-input"
                placeholder="arn:aws:iot:us-east-1:123456789012:thinggroup/Production"
                value={thingGroupArn}
                onChange={(e) => onThingGroupArn(e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const ReviewItem = ({ icon: Icon, label, value }) => (
  <div className="review-item">
    <div className="review-icon"><Icon size={16} /></div>
    <div>
      <div className="review-label">{label}</div>
      <div className="review-value">{value}</div>
    </div>
  </div>
);

const ReviewCard = ({ platformName, deviceGroup, strategy, batchSize }) => (
  <section className="card">
    <h3 className="card-title">4. Review &amp; Confirm</h3>
    <p className="card-sub">Please review all settings before starting the deployment.</p>
    <div className="review-grid">
      <ReviewItem icon={Cloud} label="Platform" value={platformName} />
      <ReviewItem icon={Users} label="Device Group" value={deviceGroup || "None registered"} />
      <ReviewItem icon={Box} label="Strategy" value={strategy} />
      <ReviewItem icon={Box} label="Batch Size" value={batchSize} />
    </div>
  </section>
);

const DeviceGroupCard = ({ groups, loading, deviceGroup, onSelect, selectedGroup }) => (
  <section className="card">
    <h3 className="card-title">2. Select Device Group</h3>
    <p className="card-sub">Registered in ota_device_groups for this platform.</p>

    <div className="field-label" style={{ marginBottom: 6 }}>Device Group</div>
    <div className="select-row">
      <select
        className="select-native"
        value={deviceGroup}
        onChange={(e) => onSelect(e.target.value)}
        disabled={loading || groups.length === 0}
      >
        {groups.length === 0 && <option value="">No device groups registered</option>}
        {groups.map((g) => (
          <option key={g.id} value={g.name}>{g.name}</option>
        ))}
      </select>
    </div>

    <div className="device-group-box">
      <div className="dg-left">
        <Users size={18} />
        <div>
          <div className="dg-title">Devices in this group</div>
          <div className="dg-count">{selectedGroup ? selectedGroup.device_count : 0}</div>
        </div>
      </div>
      <div className="dg-right">
        <div className="dg-online">
          <span className="dot" />
          {selectedGroup ? `${selectedGroup.online_count} online` : "No data"}
        </div>
        {!selectedGroup && (
          <div className="dg-pct">Register this group via /api/ota/device-groups</div>
        )}
      </div>
    </div>
  </section>
);

const OptionSelect = ({ label, value, options, onChange }) => (
  <>
    <div className="field-label">{label}</div>
    <select className="select-native full" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  </>
);

const SettingsCard = ({ settings, onChange }) => (
  <section className="card">
    <h3 className="card-title">3. Deployment Settings</h3>

    <OptionSelect label="Deployment Strategy" value={settings.strategy} options={STRATEGIES} onChange={(v) => onChange("strategy", v)} />

    <div className="two-col">
      <div>
        <OptionSelect label="Batch Size" value={settings.batchSize} options={BATCH_SIZES} onChange={(v) => onChange("batchSize", v)} />
      </div>
      <div>
        <OptionSelect label="Health Check Timeout" value={settings.healthCheck} options={HEALTH_CHECK_OPTS} onChange={(v) => onChange("healthCheck", v)} />
      </div>
    </div>

    <div className="toggle-row">
      <button type="button" className={`toggle ${settings.rollback ? "on" : ""}`} onClick={() => onChange("rollback", !settings.rollback)}>
        <span className="knob" />
      </button>
      <span className="toggle-label">Rollback on Failure</span>
    </div>

    <OptionSelect label="Max Retry Attempts" value={settings.maxRetry} options={MAX_RETRY_OPTS} onChange={(v) => onChange("maxRetry", v)} />
  </section>
);

const SummaryCard = ({ items, rollback }) => (
  <section className="card">
    <h3 className="card-title">Deployment Summary</h3>
    <div className="summary-list">
      {items.map(({ icon: Icon, label, value }) => (
        <div className="summary-item" key={label}>
          <div className="summary-icon"><Icon size={14} /></div>
          <div>
            <div className="summary-label">{label}</div>
            <div className="summary-value">{value}</div>
          </div>
        </div>
      ))}
      <div className="summary-item">
        <div className="summary-icon"><ShieldCheck size={14} /></div>
        <div>
          <div className="summary-label">Rollback Enabled</div>
          <div className="summary-value ok"><Check size={12} /> {rollback ? "Yes" : "No"}</div>
        </div>
      </div>
    </div>
  </section>
);

const ChecksCard = ({ verifications, ready }) => (
  <section className="card">
    <h3 className="card-title">Pre-Deployment Checks</h3>
    <div className="verify-list">
      {verifications.map(([label, ok]) => (
        <div className="verify-item" key={label}>
          <div className="verify-left">
            {ok ? <CheckCircle2 size={15} className="verify-check" /> : <Circle size={15} color="#D1D5DB" />}
            <span>{label}</span>
          </div>
          <span className="verify-status">{ok ? "Yes" : "No"}</span>
        </div>
      ))}
    </div>

    {ready ? (
      <div className="ready-box">
        <CheckCircle2 size={16} className="ready-check" />
        <span>Artifact is signed and ready for deployment.</span>
      </div>
    ) : (
      <div className="warn-box" style={{ marginBottom: 0 }}>
        <AlertTriangle size={16} />
        <span>Not ready — this artifact hasn't been signed in this session.</span>
      </div>
    )}
  </section>
);

const ResultCard = ({ result }) => (
  <section className="card">
    <h3 className="card-title">Deployment Result</h3>
    <div className={`result-box status-${result.status}`}>
      <div className="result-status">{result.status || "error"}</div>
      <div className="result-detail">{result.detail}</div>
      {result.deployment_id && (
        <div className="result-meta">Deployment ID: {result.deployment_id}</div>
      )}
    </div>
  </section>
);

// ---- Component ------------------------------------------------------------
export default function OTADeployment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("OTA Deployment");

  const {
    artifactId,
    manifest,
    selectedMethod,
    algorithm,
    hashAlgorithm,
    signature,
    signedAt,
  } = location.state || {};

  const hasSignedArtifact = !!manifest && !!signature;

  const [platform, setPlatform] = useState("aws");
  const [settings, setSettings] = useState(INITIAL_SETTINGS);
  const [thingGroupArn, setThingGroupArn] = useState("");
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);

  const { deviceGroups, loadingGroups, deviceGroup, setDeviceGroup } = useDeviceGroups(platform);

  const selected = PLATFORMS.find((p) => p.id === platform);
  const selectedGroup = deviceGroups.find((g) => g.name === deviceGroup);

  const updateSetting = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const handleTabClick = (tab) => {
    const route = TAB_ROUTES[tab];
    if (route) navigate(route, { state: location.state });
    else setActiveTab(tab);
  };

  const handleDeploy = async () => {
    if (!hasSignedArtifact) {
      alert("Sign an artifact first — there's nothing to deploy yet.");
      return;
    }

    setDeploying(true);
    setDeployResult(null);

    try {
      const response = await fetch(`${API_URL}/api/ota/deploy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          artifact_id: artifactId,
          platform,
          device_group: deviceGroup,
          strategy: settings.strategy,
          batch_size: settings.batchSize,
          health_check_minutes: settings.healthCheck,
          max_retry: settings.maxRetry,
          rollback_enabled: settings.rollback,
          thing_group_arn: thingGroupArn || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Deployment request failed.");

      setDeployResult(data);
    } catch (err) {
      setDeployResult({ success: false, status: "error", detail: err.message });
    } finally {
      setDeploying(false);
    }
  };

  // Only claims backed by real state — dropped "Certificate Verification:
  // Valid", "Compatibility Check: Passed", "Security Scan: No Threats
  // Found" from the previous version. Nothing in this flow runs any of
  // those checks.
  const VERIFICATIONS = [
    ["Artifact Signed", hasSignedArtifact],
    ["Manifest Present", !!manifest],
  ];

  const SUMMARY_ITEMS = buildSummaryItems({
    manifest, algorithm, selectedMethod, signedAt, hashAlgorithm, selectedGroup,
  });

  return (
    <div className="ota-root">
      <style>{CSS}</style>

      <div className="wrap">
        <TopTabs activeTab={activeTab} onSelect={handleTabClick} />
      </div>

      <main className="wrap page">
        <h2 className="page-title">OTA Deployment Configuration</h2>
        <p className="page-sub">Configure and deploy your signed firmware to target devices.</p>

        {!hasSignedArtifact && (
          <div className="warn-box">
            <AlertTriangle size={16} />
            <span>No signed artifact in this session — sign one first, then come back here to deploy it.</span>
          </div>
        )}

        <Stepper />

        <div className="grid">
          <div className="col-main">
            <PlatformCard
              platform={platform}
              selected={selected}
              onSelect={setPlatform}
              thingGroupArn={thingGroupArn}
              onThingGroupArn={setThingGroupArn}
            />
            <ReviewCard
              platformName={selected.name}
              deviceGroup={deviceGroup}
              strategy={settings.strategy}
              batchSize={settings.batchSize}
            />
          </div>

          {/* Middle column */}
          <div className="col-mid">
            <DeviceGroupCard
              groups={deviceGroups}
              loading={loadingGroups}
              deviceGroup={deviceGroup}
              onSelect={setDeviceGroup}
              selectedGroup={selectedGroup}
            />
            <SettingsCard settings={settings} onChange={updateSetting} />
          </div>

          {/* Right column */}
          <div className="col-side">
            <SummaryCard items={SUMMARY_ITEMS} rollback={settings.rollback} />
            <ChecksCard verifications={VERIFICATIONS} ready={hasSignedArtifact} />
            {deployResult && <ResultCard result={deployResult} />}

            <div className="actions-row">
              <button className="btn-primary" type="button" onClick={handleDeploy} disabled={deploying || !hasSignedArtifact}>
                <Rocket size={15} /> {deploying ? "Deploying..." : "Start Deployment"}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  topTabs: { display: "flex", gap: 28, borderBottom: "1px solid #E8E9F1", marginBottom: 20, paddingBottom: 0 },
  topTabBtn: { background: "none", border: "none", fontSize: 14, fontWeight: 600, padding: "0 0 12px 0", cursor: "pointer" },
};

// ---- Styles ----------------------------------------------------------------
const CSS = `
.ota-root {
  --purple: #5B4FE0;
  --purple-dark: #4B3ED1;
  --purple-bg: #F3F1FE;
  --border: #E8E9F1;
  --text: #1A1D29;
  --text-sub: #8B92A8;
  --green: #16A34A;
  --green-bg: #ECFDF3;
  --amber: #B45309;
  --amber-bg: #FFFBEB;
  --bg: #F7F7FA;
  --card-bg: #FFFFFF;
  --max-width: 1600px;
  width: 100%;
  min-height: 100%;
  color: var(--text);
  background: #fff;
  box-sizing: border-box;
}
.ota-root *, .ota-root *::before, .ota-root *::after { box-sizing: border-box; }
.wrap { width: 100%; max-width: var(--max-width); margin: 0 auto; }

.page { position: relative; padding: 24px 26% 24px 24px; }
.page-title { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
.page-sub { font-size: 13px; color: var(--text-sub); margin: 0 0 16px; }

.warn-box {
  display: flex; align-items: center; gap: 8px;
  background: var(--amber-bg); color: var(--amber);
  border-radius: 8px; padding: 10px 12px;
  font-size: 12.5px; font-weight: 600; margin-bottom: 16px;
}

.stepper { display: flex; align-items: flex-start; margin-bottom: 26px; }
.step { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 0 0 auto; width: 160px; }
.step-circle {
  width: 26px; height: 26px; border-radius: 50%;
  background: #E9E9F1; color: #8B8B9A;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 600; flex-shrink: 0;
}
.step-circle.current { background: var(--purple); color: #fff; }
.step-label { font-size: 12px; color: #9A9AAA; text-align: center; line-height: 1.3; max-width: 140px; }
.step-label.current { color: var(--text); font-weight: 600; }
.step-line { flex: 1 1 auto; height: 1px; background: var(--border); margin: 13px 0 0; min-width: 20px; }

.grid { display: grid; grid-template-columns: 1.15fr 1fr; gap: 18px; align-items: start; }
.col-main { display: contents; }
.col-mid { display: flex; flex-direction: column; gap: 18px; grid-column: 2; grid-row: 1; }
.col-main > .card:first-child { grid-column: 1; grid-row: 1; }
.col-main > .card:nth-child(2) { grid-column: 1 / 3; grid-row: 2; }
.col-side { position: absolute; top: 24px; right: 24px; width: 23%; display: flex; flex-direction: column; gap: 18px; }

.card { background: var(--card-bg); border: 1px solid var(--border); border-radius: 12px; padding: 20px; }
.card-title { font-size: 14px; font-weight: 700; margin: 0 0 4px; }
.card-sub { font-size: 12.5px; color: var(--text-sub); margin: 0 0 16px; }

.platform-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 18px; }
.platform-tile {
  position: relative; border: 1.5px solid var(--border); border-radius: 10px;
  background: #fff; padding: 14px 10px; display: flex; flex-direction: column;
  align-items: flex-start; gap: 6px; cursor: pointer; text-align: left; font-family: inherit;
}
.platform-tile.active { border-color: var(--purple); background: var(--purple-bg); }
.radio {
  position: absolute; top: 10px; right: 10px; width: 15px; height: 15px; border-radius: 50%;
  border: 1.5px solid #C9C9D6; display: flex; align-items: center; justify-content: center;
}
.platform-tile.active .radio { border-color: var(--purple); }
.radio-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--purple); }
.platform-icon { width: 30px; height: 30px; border-radius: 8px; background: #F1F0FA; color: var(--purple); display: flex; align-items: center; justify-content: center; }
.platform-tile.active .platform-icon { background: #fff; }
.platform-name { font-size: 12.5px; font-weight: 600; }
.platform-sub { font-size: 11px; color: var(--text-sub); }

.selected-heading { font-size: 13px; font-weight: 700; margin: 0 0 10px; }
.selected-card { border: 1px solid var(--border); border-radius: 10px; padding: 14px; }
.selected-top { display: flex; justify-content: space-between; align-items: flex-start; }
.selected-left { display: flex; gap: 10px; }
.selected-icon {
  width: 30px; height: 30px; border-radius: 8px; background: var(--purple-bg); color: var(--purple);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.selected-name { font-size: 13px; font-weight: 700; }
.selected-desc { font-size: 11.5px; color: var(--text-sub); max-width: 320px; margin-top: 2px; }
.badge-connected {
  display: flex; align-items: center; gap: 4px; background: var(--green-bg); color: var(--green);
  font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap;
}
.badge-not-connected {
  display: flex; align-items: center; gap: 4px; background: var(--amber-bg); color: var(--amber);
  font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 20px; white-space: nowrap;
}
.field-label { font-size: 11.5px; color: var(--text-sub); margin-bottom: 4px; }
.field-value { font-size: 12.5px; font-weight: 600; word-break: break-all; }
.text-input {
  width: 100%; border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px;
  font-size: 12.5px; font-family: inherit; color: var(--text);
}

.select-row { display: flex; gap: 8px; margin-bottom: 16px; }
.select-native {
  border: 1px solid var(--border); border-radius: 8px; padding: 9px 12px; font-size: 12.5px;
  background: #fff; color: var(--text); flex: 1; font-family: inherit;
}
.select-native.full { width: 100%; margin-bottom: 14px; }
.device-group-box {
  border: 1px solid var(--border); border-radius: 10px; background: var(--purple-bg);
  padding: 14px; display: flex; justify-content: space-between; align-items: center;
}
.dg-left { display: flex; align-items: center; gap: 10px; color: var(--purple); }
.dg-title { font-size: 11.5px; color: var(--text-sub); }
.dg-count { font-size: 17px; font-weight: 700; color: var(--text); }
.dg-right { text-align: right; }
.dg-online { display: flex; align-items: center; gap: 5px; justify-content: flex-end; font-size: 11.5px; color: var(--green); font-weight: 600; }
.dot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); display: inline-block; }
.dg-pct { font-size: 11px; color: var(--text-sub); margin: 2px 0; max-width: 160px; }

.two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
.toggle-row { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.toggle {
  width: 38px; height: 21px; border-radius: 20px; border: none; background: #D8D8E2;
  cursor: pointer; position: relative; padding: 2px; display: flex; align-items: center;
}
.toggle.on { background: var(--purple); }
.knob { width: 17px; height: 17px; border-radius: 50%; background: #fff; display: block; transition: transform 0.15s ease; transform: translateX(0); }
.toggle.on .knob { transform: translateX(17px); }
.toggle-label { font-size: 12.5px; font-weight: 600; }

.summary-list { display: flex; flex-direction: column; gap: 14px; }
.summary-item { display: flex; gap: 10px; align-items: flex-start; }
.summary-icon { width: 24px; height: 24px; border-radius: 6px; background: var(--purple-bg); color: var(--purple); display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.summary-label { font-size: 11px; color: var(--text-sub); }
.summary-value { font-size: 12.5px; font-weight: 600; word-break: break-word; }
.summary-value.ok { color: var(--green); display: flex; align-items: center; gap: 4px; }

.verify-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 14px; }
.verify-item { display: flex; justify-content: space-between; align-items: center; font-size: 12.5px; }
.verify-left { display: flex; align-items: center; gap: 8px; }
.verify-check { color: var(--green); }
.verify-status { color: var(--text-sub); font-size: 11.5px; }

.ready-box {
  display: flex; align-items: center; gap: 8px; background: var(--green-bg); color: #15803D;
  border-radius: 8px; padding: 10px 12px; font-size: 12px; font-weight: 600; margin-bottom: 14px;
}
.ready-check { color: var(--green); flex-shrink: 0; }

.result-box { border-radius: 8px; padding: 10px 12px; font-size: 12.5px; }
.result-box.status-dispatched { background: var(--green-bg); color: #15803D; }
.result-box.status-pending_configuration, .result-box.status-not_configured { background: var(--amber-bg); color: var(--amber); }
.result-box.status-error { background: #FEE2E2; color: #DC2626; }
.result-status { font-weight: 700; text-transform: capitalize; margin-bottom: 4px; }
.result-detail { line-height: 1.4; }
.result-meta { margin-top: 6px; font-size: 11px; opacity: 0.8; }

.review-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.review-item { display: flex; gap: 8px; align-items: flex-start; }
.review-icon { width: 26px; height: 26px; border-radius: 7px; background: var(--purple-bg); color: var(--purple); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.review-label { font-size: 11px; color: var(--text-sub); }
.review-value { font-size: 12.5px; font-weight: 700; }

.actions-row { display: flex; gap: 10px; }
.btn-outline, .btn-primary {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
  border-radius: 8px; padding: 11px; font-size: 13px; font-weight: 600; cursor: pointer;
  border: none; font-family: inherit;
}
.btn-outline { background: #fff; border: 1px solid var(--border); color: var(--text); }
.btn-primary { background: var(--purple); color: #fff; }
.btn-primary:hover { background: var(--purple-dark); }
.btn-primary:disabled { background: #C9C9D6; cursor: not-allowed; }

@media (max-width: 800px) {
  .page { padding: 16px; }
  .grid { grid-template-columns: 1fr; }
  .col-main { display: flex; flex-direction: column; gap: 18px; }
  .col-mid { grid-column: auto; grid-row: auto; }
  .col-main > .card:first-child, .col-main > .card:nth-child(2) { grid-column: auto; grid-row: auto; }
  .col-side { position: static; width: auto; }
  .platform-grid { grid-template-columns: repeat(2, 1fr); }
  .review-grid { grid-template-columns: repeat(2, 1fr); }
}
`;